import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { saveAs } from 'file-saver';
import * as ExcelJS from 'exceljs';
import { BaseService } from 'src/app/shared/services/base.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { ResponseDefault } from 'src/app/shared/data/response-default.interface';
import { environment } from 'src/environments/environment';
import { EMPLOYEE_STATUS_INFO, employeeStatusInfo } from 'src/app/shared/data/employee-status.enum';
import { IFilterCollaboratorReport, ICollaboratorReport } from '../data/collaborator-report';

export const EXPORT_COLUMNS: { header: string; width: number }[] = [
  { header: 'Empresa', width: 30 },
  { header: 'Filial', width: 22 },
  { header: 'Colaborador', width: 30 },
  { header: 'CPF', width: 16 },
  { header: 'Situação', width: 15 },
  { header: 'Cartão Ativo', width: 13 },
  { header: 'Número do Cartão', width: 22 },
  { header: 'Validade', width: 12 }
];

// Derivado das colunas para não dessincronizar da ordem declarada acima.
const CARD_STATUS_COLUMN = EXPORT_COLUMNS.findIndex((col) => col.header === 'Cartão Ativo') + 1;

const MONTHS_PT = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
];

// Mesmos nomes do cadastro de colaborador (EMPLOYEE_STATUS_INFO): 0 é Candidato, não Desligado.
export const STATUS_FUNCIONARIO_OPTIONS = [
  { value: '', label: 'Todos' },
  ...EMPLOYEE_STATUS_INFO.map(({ value, label }) => ({ value: String(value), label }))
];

export function statusLabel(status: string | number): string {
  if (status === '' || status === null || status === undefined) return '';
  return employeeStatusInfo(Number(status))?.label ?? String(status);
}

/**
 * Monta a planilha em memória. Separada do download para poder ser verificada em teste.
 */
export function buildCollaboratorWorkbook(
  data: ICollaboratorReport[],
  companyName: string | null = null
): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Colaboradores');
  const TOTAL_COLS = EXPORT_COLUMNS.length;

  EXPORT_COLUMNS.forEach((col, i) => {
    ws.getColumn(i + 1).width = col.width;
  });

  const now = new Date();
  const monthYear = `${MONTHS_PT[now.getMonth()].toUpperCase()}/${String(now.getFullYear()).slice(2)}`;
  const companyLabel = companyName && companyName !== 'Todas' ? companyName.toUpperCase() : 'TODOS';

  // Linha 1: título
  ws.addRow([`Colaboradores - ${companyLabel} - ${monthYear}`]);
  ws.mergeCells(1, 1, 1, TOTAL_COLS);
  const titleRow = ws.getRow(1);
  titleRow.height = 28;
  titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FF1A2E44' } };
  titleRow.getCell(1).alignment = { vertical: 'middle' };

  // Linha 2: subtítulo
  ws.addRow(['Olive Saúde · Status de cartão']);
  ws.mergeCells(2, 1, 2, TOTAL_COLS);
  const subtitleRow = ws.getRow(2);
  subtitleRow.height = 16;
  subtitleRow.getCell(1).font = { size: 10, color: { argb: 'FF6B7280' } };

  // Linha 3: espaço
  ws.addRow([]);
  ws.getRow(3).height = 8;

  // Linha 4: cabeçalho
  ws.addRow(EXPORT_COLUMNS.map((col) => col.header));
  const headerRow = ws.getRow(4);
  headerRow.height = 22;
  const thinBorder: Partial<ExcelJS.Borders> = {
    top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
    left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
    bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
    right: { style: 'thin', color: { argb: 'FFD1D5DB' } }
  };

  headerRow.eachCell((cell: ExcelJS.Cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A596F' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = thinBorder;
  });

  // Linhas de dados
  data.forEach((c, i) => {
    const bgArgb = i % 2 === 0 ? 'FFFFFFFF' : 'FFF0F8FA';
    const row = ws.addRow([
      c.companyName ?? '',
      c.subsidiaryName ?? '',
      c.employeeName ?? '',
      c.cpf ?? '',
      statusLabel(c.status),
      c.hasActiveCard ? 'Sim' : 'Não',
      c.cardNumber ?? '',
      c.cardExpiration ?? ''
    ]);
    row.height = 18;
    row.eachCell({ includeEmpty: true }, (cell: ExcelJS.Cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgArgb } };
      cell.alignment = { vertical: 'middle' };
      cell.border = thinBorder;
    });

    row.getCell(CARD_STATUS_COLUMN).font = { color: { argb: c.hasActiveCard ? 'FF1A596F' : 'FFE53935' } };
  });

  return wb;
}

@Injectable({
  providedIn: 'root'
})
export class CollaboratorReportService extends BaseService {
  private readonly apiUrl = `${environment.apiUrlBase}${environment.microservices.card}report/collaborator`;

  constructor(
    private readonly http: HttpClient,
    protected readonly toast: ToastService
  ) {
    super(toast);
  }

  getCollaboratorReport(filter: IFilterCollaboratorReport): Observable<ResponseDefault<ICollaboratorReport[]>> {
    let params = new HttpParams().appendAll({
      pageNumber: filter.pageNumber.toString(),
      pageSize: filter.pageSize.toString(),
      orderBy: filter.orderBy.toString()
    });

    if (filter.cardStatus !== null && filter.cardStatus !== undefined) {
      params = params.append('cardStatus', filter.cardStatus.toString());
    }
    if (filter.searchText) {
      params = params.append('searchText', filter.searchText);
    }
    if (filter.companyName) {
      params = params.append('companyName', filter.companyName);
    }
    if (filter.statusFuncionario !== null && filter.statusFuncionario !== undefined) {
      params = params.append('statusFuncionario', filter.statusFuncionario.toString());
    }

    return this.http.get<ResponseDefault<ICollaboratorReport[]>>(this.apiUrl, { params });
  }

  async exportXlsx(data: ICollaboratorReport[], companyName: string | null = null): Promise<void> {
    const wb = buildCollaboratorWorkbook(data, companyName);

    const buffer = await wb.xlsx.writeBuffer();
    const suffix = companyName && companyName !== 'Todas' ? `-${companyName}` : '';

    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `relatorio-colaboradores${suffix}.xlsx`
    );
  }
}
